// Conversar — the live voice + text conversation surface.
//
// Wraps the existing AsisClient and exposes:
//  - Start / Stop button (voice session)
//  - Mute, Interrupt, PTT
//  - Text composer + send
//  - Live transcript with final/partial distinction
//  - Collapsible details: provider, model, sample rate, state, cost, latency
//  - Live audio meter (CSS var --rms, set by AsisClient)
//
// The face SVG is a calm neutral "listening" indicator. We never animate
// the mouth to fake latency; the mouth path changes only on real state
// transitions (listening / thinking / speaking / error).
//
// No AudioContext is created until the user clicks Iniciar. No
// microphone permission is requested at any other moment.

import { AsisClient, type SessionState } from '../../client.js';
import { LocalConversationCoordinator } from '../local-conversation.js';
import { createConversationCards, takeConversationStart } from '../components/conversation-cards.js';
import { ConversationCamera } from '../../camera.js';
import type { ApiClient, ChatSource } from '../../api/client.js';
import type { DesktopBridge } from '../../session/controller.js';
import captureWorkletUrl from '../../audio/capture-worklet.ts?worker&url';
import playbackWorkletUrl from '../../audio/playback-worklet.ts?worker&url';
import { MIC_SAMPLES, SAMPLE_RATE } from '../../../protocol/constants.js';
import { takeConversationIntent } from '../conversation-intent.js';
import { faceMark } from '../components/face-mark.js';
import { icon } from '../components/icons.js';
import { h, clear, toast, type View } from '../components/dom.js';

const STATUS_LABELS: Record<SessionState, string> = {
  disconnected: 'Cuando quieras',
  connecting: 'Conectando…',
  listening: 'Te escucho',
  thinking: 'Pensando…',
  speaking: 'Hablando',
  muted: 'Micrófono silenciado',
  interrupted: 'Te escucho',
  paused: 'En pausa',
  error: 'Algo ha fallado',
};

const MOUTH_PATHS: Record<SessionState, string> = {
  disconnected: 'M102 184 L138 184', connecting: 'M109 184 L131 184',
  listening: 'M104 182 Q120 190 136 182', thinking: 'M109 184 L131 184',
  speaking: 'M102 180 Q120 202 138 180 Z', muted: 'M102 184 L138 184',
  interrupted: 'M102 184 L138 184', paused: 'M102 184 L138 184', error: 'M109 184 L131 184',
};

export interface ConversarContext {
  serverBase: string;
  voiceConfig: { provider: string; providerModel: string; sampleRate: number; providerConfigured?: boolean } | null;
  userDisplayName: string | null;
  getToken: () => string | null;
  onLogout?: () => void;
  api?: ApiClient;
  localBridge?: DesktopBridge['local'];
  log: (msg: string, data?: unknown) => void;
}

export function createConversarView(ctx: ConversarContext): View {
  let destroyed = false;
  const root = h('section', { class: 'view view-conversar', id: 'view-conversar' });
  const face = h('div', { class: 'face', 'aria-hidden': 'true' });
  const facePlate = h('div', { class: 'face-plate', 'data-state': 'disconnected' });
  facePlate.innerHTML = faceMark('face-mouth');
  face.appendChild(facePlate);

  const statusChip = h('div', { class: 'status-chip', 'aria-live': 'polite' });
  const statusDot = h('span', { class: 'status-dot' });
  const statusText = h('span', { class: 'status-text' }, ['Cuando quieras']);
  statusChip.append(statusDot, statusText);

  const memoryLink = h('a', { href: '#/memoria', class: 'btn ghost small' });
  memoryLink.innerHTML = `${icon('memory')}<span>Recuerdos</span>`;
  memoryLink.setAttribute('aria-label', 'Recuerdos');
  const options = h('div', { class: 'conversation-options-panel' });
  const optionsRoot = h('details', { class: 'conversation-options' }, [h('summary', {}, ['Opciones']), options]);
  const head = h('header', { class: 'chat-head' }, [
    face,
    h('div', { class: 'chat-head-text' }, [h('h1', {}, ['Habla con ASIS']), statusChip]),
    h('div', { class: 'chat-head-actions' }, [memoryLink, optionsRoot]),
  ]);
  root.appendChild(head);
  if (!ctx.voiceConfig) head.append(h('p', { class: 'card-sub chat-head-note' }, ['La voz no está disponible ahora. Puedes escribirme.']));

  const voiceBar = h('div', { class: 'chat-voice', hidden: true });
  root.appendChild(voiceBar);

  const cta = h('button', { id: 'btn-primary', class: 'btn ghost composer-talk', type: 'button' });
  const setCta = (label: string, iconName: 'mic' | 'stop' = 'mic') => { cta.innerHTML = `${icon(iconName)}<span>${label}</span>`; cta.setAttribute('aria-label', label); };
  setCta('Hablar');
  const writeButton = h('button', { class: 'btn ghost', type: 'button', id: 'btn-write' }, ['Escribir']);
  options.append(writeButton);

  const engineLabel = h('label', {class:'field'}, [h('span',{},['Conversación'])]);
  const engine = h('select',{id:'conversation-engine',class:'select'});
  const remoteOption=h('option',{value:'gemini'},['Voz en directo']);
  const localOption=h('option',{value:'local',disabled:true},['Voz local · experimental']);
  engine.append(remoteOption,localOption);engineLabel.append(engine);options.append(engineLabel);
  const localHelp=h('p',{class:'card-sub'},['Procesa la voz en este Mac. Se prepara desde Mi cuenta.']);
  localHelp.hidden=true;options.append(localHelp);
  const wakeLabel=h('label',{class:'mode-option',hidden:true});
  const wake=h('input',{id:'wake-enabled',type:'checkbox'});
  wakeLabel.append(wake,document.createTextNode(' Esperar Oye ASIS · experimental'));options.append(wakeLabel);
  if(ctx.localBridge&&ctx.api)void ctx.localBridge.status().then(status=>{
    if(destroyed)return;localOption.disabled=!status.whisper.available||!status.whisper.installed||!status.tts.available;
  }).catch(()=>{if(!destroyed)localHelp.textContent='El estado de los modelos locales no está disponible. Revisa Perfil.';});

  // mode + text-only toggles
  const modeRow = h('div', { class: 'mode-row' });
  const modeLegend = h('span', { class: 'mode-legend' }, ['Modo']);
  const vad = h('label', { class: 'mode-option' }, []);
  const vadR = h('input', { type: 'radio', name: 'mode', value: 'vad' });
  vadR.checked = true;
  vad.append(vadR, document.createTextNode(' Voz continua'));
  const ptt = h('label', { class: 'mode-option' }, []);
  const pttR = h('input', { type: 'radio', name: 'mode', value: 'ptt' });
  ptt.append(pttR, document.createTextNode(' Pulsa para hablar'));
  const textOnly = h('label', { class: 'mode-option' }, []);
  const textOnlyCkb = h('input', { type: 'checkbox', id: 'text-only' });
  textOnly.append(textOnlyCkb, document.createTextNode(' Solo texto'));
  modeRow.append(modeLegend, vad, ptt, textOnly);
  options.appendChild(modeRow);

  // controls row (mute, interrupt, close, ptt)
  const secondary = h('div', { class: 'conversar-secondary' });
  const controls = h('div', { class: 'controls' });
  const btnMute = h('button', { id: 'btn-mute', class: 'btn ghost small', type: 'button', 'aria-pressed': 'false' }, ['Silenciar']);
  const btnInterrupt = h('button', { id: 'btn-interrupt', class: 'btn ghost small', type: 'button' }, ['Interrumpir']);
  const btnClose = h('button', { id: 'btn-close', class: 'btn ghost small', type: 'button', hidden: true }, ['Finalizar conversación']);
  btnMute.disabled = true; btnInterrupt.disabled = true; btnClose.disabled = true;
  controls.append(btnMute, btnInterrupt, btnClose);
  secondary.appendChild(controls);
  const cameraToggle = h('button', { id: 'camera-toggle', class: 'btn ghost', disabled: true, 'aria-pressed': 'false', 'aria-describedby': 'camera-help' }, ['Activar cámara']);
  const cameraHelp = h('p', { id: 'camera-help', class: 'card-sub' }, ['La cámara solo se comparte si la activas durante una conversación de voz compatible.']);
  const cameraVideo = h('video', { class: 'remote-camera-preview', autoplay: true, muted: true, playsinline: true, hidden: true, 'aria-label': 'Vista previa de cámara' }) as HTMLVideoElement;
  secondary.append(cameraToggle, cameraHelp, cameraVideo);

  const pttCard = h('div', { id: 'ptt-card', class: 'ptt-card', hidden: true });
  const btnPtt = h('button', { id: 'btn-ptt', class: 'btn ptt', type: 'button', 'aria-label': 'Pulsar para hablar' }, ['Mantén pulsado']);
  pttCard.append(btnPtt, h('p', { class: 'ptt-hint' }, ['También la barra espaciadora fuera de un campo de texto.']));
  voiceBar.after(pttCard);

  // meter
  const meter = h('div', { class: 'meter', 'aria-hidden': 'true' });
  const meterTrack = h('div', { class: 'meter-track' });
  const meterFill = h('div', { id: 'meter-fill', class: 'meter-fill' });
  meterTrack.appendChild(meterFill);
  const meterLabel = h('p', { id: 'meter-label', class: 'meter-label' }, ['Sin entrada de audio']);
  meter.append(meterTrack, meterLabel);
  secondary.appendChild(meter);

  // details collapsible
  const details = h('details', { class: 'details' });
  const summary = h('summary', {}, ['Detalles de la sesión']);
  const kvGrid = h('dl', { class: 'kv' });
  const mkKv = (id: string) => {
    const dt = h('dt', {}, ['']);
    const dd = h('dd', { id: `kv-${id}` }, ['—']);
    return { dt, dd, wrap: h('div', { class: 'kv-row' }, [dt, dd]) };
  };
  const kvs = {
    provider: mkKv('provider'),
    model: mkKv('model'),
    rate: mkKv('rate'),
    state: mkKv('state'),
    cost: mkKv('cost'),
    latency: mkKv('latency'),
  };
  kvs.provider.dt.textContent = 'Servicio de voz';
  kvs.model.dt.textContent = 'Modelo';
  kvs.rate.dt.textContent = 'Muestreo';
  kvs.state.dt.textContent = 'Estado';
  kvs.cost.dt.textContent = 'Coste estimado';
  kvs.latency.dt.textContent = 'Latencia';
  for (const k of Object.values(kvs)) kvGrid.appendChild(k.wrap);
  kvs.rate.wrap.hidden = true;
  details.append(summary, kvGrid);
  if (ctx.voiceConfig) {
    kvs.provider.dd.textContent = 'Voz en directo';
    kvs.model.dd.textContent = 'Configurado en tu cuenta';
    kvs.rate.dd.textContent = `${SAMPLE_RATE} Hz · ${MIC_SAMPLES} muestras/frame`;
  }
  secondary.appendChild(details);
  options.appendChild(secondary);

  // The conversation: one centred column, with what to say next right above the composer.
  const scroll = h('div', { class: 'chat-scroll' });
  const column = h('div', { class: 'chat-column' });
  scroll.append(column);
  root.appendChild(scroll);

  const transcriptList = h('div', { id: 'transcript-list', class: 'transcript', 'aria-live': 'polite', 'aria-relevant': 'additions', role: 'log' });
  const transcriptHint = h('div', { id: 'transcript-hint', class: 'transcript-hint' }, [h('p', {}, ['Pregúntame lo que quieras de tu día, tus clientes o tu correo.'])]);
  column.append(transcriptHint, transcriptList);

  const starters = h('div', { class: 'chat-chips' });
  const composer = h('div', { class: 'composer' });
  const composerInput = h('input', { id: 'composer-input', class: 'composer-input', type: 'text', placeholder: 'Escribe a ASIS…', 'aria-label': 'Mensaje de texto' });
  const btnSend = h('button', { id: 'btn-send', class: 'btn primary composer-send', type: 'button', 'aria-label': 'Enviar mensaje' });
  btnSend.innerHTML = icon('arrowUp');
  composer.append(composerInput, cta, btnSend);
  root.appendChild(h('div', { class: 'chat-foot' }, [starters, composer]));
  composerInput.disabled = !ctx.api; btnSend.disabled = !ctx.api; btnPtt.disabled = true;
  const cards = ctx.api ? createConversationCards(ctx.api, transcriptList, composerInput, btnSend, transcriptHint, busy => { if (!started && !connecting) paintState(busy ? 'thinking' : 'disconnected'); }) : null;
  writeButton.hidden = Boolean(cards);
  function renderStarters(phrases: string[]): void {
    starters.replaceChildren(...phrases.map(text => {
      const starter = h('button', { class: 'chip', type: 'button' }, [text]);
      starter.addEventListener('click', () => { if (cards) void cards.submit(text); });
      return starter;
    }));
  }
  if (cards) {
    renderStarters(['¿Qué tengo para hoy?', 'Conecta mi CRM', 'Conecta mi correo']);
    void ctx.api!.listConnectors().then(({ connectors }) => {
      if (destroyed || !connectors.some(connector => connector.status === 'enabled')) return;
      renderStarters(['¿Qué tengo para hoy?', '¿Qué facturas tengo pendientes?', '¿Qué clientes tengo?']);
    }).catch(() => { /* the starting suggestions still work */ });
  }

  // runtime state
  let client: AsisClient | null = null;
  let localCoordinator: LocalConversationCoordinator | null = null;
  let selectedConversation: 'gemini' | 'local' = 'gemini';
  const camera=new ConversationCamera(cameraVideo,data=>client?.sendImage(data),message=>{cameraHelp.textContent=message;cameraToggle.setAttribute('aria-pressed','false');cameraToggle.textContent='Activar cámara';});
  let localTranscriptId=0;
  let audioCtx: AudioContext | null = null;
  let started = false;
  let connecting = false;
  let startAttempt = 0;
  let lastUsageCost: number | null = null;
  let currentMode: 'vad' | 'ptt' = 'vad';
  let textOnlyMode = false;
  const transcriptBubbles = new Map<string, HTMLDivElement>();
  const transcriptText = new Map<string, string>();
  let meterTimer: number | null = null;
  let keyboardPtt = false;
  let captureUrls: { capture: string; playback: string } | null = null;
  let wsUrl: string | null = null;

  function paintState(state: SessionState): void {
    statusDot.setAttribute('data-state', state);
    facePlate.setAttribute('data-state', state);
    statusText.textContent = STATUS_LABELS[state] ?? state;
    kvs.state.dd.textContent = STATUS_LABELS[state];
    const mouth = face.querySelector('#face-mouth') as SVGPathElement | null;
    if (mouth) mouth.setAttribute('d', MOUTH_PATHS[state] ?? MOUTH_PATHS.disconnected);
    const eyes = face.querySelector('.face-eyes') as SVGGElement | null;
    if (eyes) eyes.style.opacity = state === 'error' ? '0.5' : '1';
  }

  function setStatus(state: SessionState): void {
    if (state === 'disconnected') {camera.stop();setControlsForStarted(false);}
    paintState(state);
    if (state === 'disconnected' || state === 'error') {
      btnPtt.classList.remove('active');
    }
  }

  function setControlsForStarted(running: boolean): void {
    started = running;
    connecting = false;
    cta.disabled = false;
    setCta(running ? 'Detener' : textOnlyMode ? 'Empezar a escribir' : 'Hablar', running ? 'stop' : 'mic');
    writeButton.hidden = running || textOnlyMode || Boolean(cards);
    controls.hidden = !running;
    root.dataset['voice'] = running ? 'on' : 'off';
    voiceBar.hidden = !running;
    setTimeout(() => { scroll.scrollTop = scroll.scrollHeight; }, 0);
    if (running) voiceBar.append(controls); else secondary.prepend(controls);
    btnMute.disabled = !running || textOnlyMode;
    btnInterrupt.disabled = !running;
    btnClose.disabled = !running;
    btnSend.disabled = !ctx.api && !running;
    btnPtt.disabled = !running || textOnlyMode;
    composerInput.disabled = !ctx.api && !running;
    vadR.disabled = running;
    pttR.disabled = running;
    textOnlyCkb.disabled = running;
    engine.disabled=running;wake.disabled=running||textOnlyMode;
    cameraToggle.disabled=!running||selectedConversation!=='gemini'||ctx.voiceConfig?.provider!=='gemini-live';
    pttCard.hidden = currentMode !== 'ptt';
    if (!running) {
      btnMute.setAttribute('aria-pressed', 'false');
      btnMute.textContent = 'Silenciar';
    }
  }

  const bridgedIntents = new Set<string>();
  function logTranscription(role: 'user' | 'assistant', text: string, final: boolean, id: string, interrupted?: boolean): void {
    if (!text) return;
    if (final && role === 'assistant' && cards && bridgedIntents.delete('local-setup')) void cards.reload().catch(() => {});
    if (final && role === 'user' && cards && !bridgedIntents.has(id) && /\b(conecta|vincula)\b.{0,60}\b(crm|correo|gmail|outlook|holded|hubspot|herramienta)\b/i.test(text)) { bridgedIntents.add(id); if (selectedConversation === 'local') bridgedIntents.add('local-setup'); else { void cards.submit(text); return; } }
    transcriptHint.hidden = true;
    const key = `${role}:${id}`;
    let bubble = transcriptBubbles.get(key);
    if (!bubble) {
      bubble = h('div', { class: `bubble ${role}` });
      transcriptBubbles.set(key, bubble);
      transcriptList.appendChild(bubble);
    }
    const accumulated = final ? text : `${transcriptText.get(key) ?? ''}${text}`;
    transcriptText.set(key, accumulated);
    bubble.textContent = accumulated;
    if (interrupted) bubble.classList.add('interrupted');
    if (final) bubble.dataset['final'] = '1';
    scroll.scrollTop = scroll.scrollHeight;
  }

  function setUsage(estimatedUsd: number | undefined, inputTokens?: number, outputTokens?: number): void {
    if (typeof estimatedUsd === 'number') {
      lastUsageCost = (lastUsageCost ?? 0) + estimatedUsd;
      kvs.cost.dd.textContent = `$${lastUsageCost.toFixed(4)}`;
    } else if (inputTokens !== undefined || outputTokens !== undefined) {
      kvs.cost.dd.textContent = 'No disponible';
    } else {
      kvs.cost.dd.textContent = '—';
    }
  }

  function setMetrics(firstAudioMs: number | undefined): void {
    if (typeof firstAudioMs === 'number') kvs.latency.dd.textContent = `${firstAudioMs} ms`;
  }

  function onError(code: string, message: string): void {
    if(code==='image_rate_limited'){cameraHelp.textContent=message;return;}
    if(code==='unsupported_image'||code==='bad_image'){camera.stop();cameraHelp.textContent=message;cameraToggle.disabled=true;cameraToggle.setAttribute('aria-pressed','false');return;}
    setStatus('error');
    transcriptHint.hidden = true;
    transcriptList.appendChild(h('div', { class: 'bubble assistant interrupted' }, [message]));
    scroll.scrollTop = scroll.scrollHeight;
  }

  function readMode(): 'vad' | 'ptt' { return pttR.checked ? 'ptt' : 'vad'; }

  async function start(): Promise<void> {
    if (started || connecting) return;
    if (!captureUrls || !wsUrl) {
      toast('Configuración de audio no disponible', 'error');
      return;
    }
    const attempt = ++startAttempt;
    transcriptBubbles.clear();
    transcriptText.clear();
    if (!cards) clear(transcriptList);
    transcriptHint.hidden = transcriptList.children.length > 0;
    lastUsageCost = null;
    kvs.cost.dd.textContent = '—';
    kvs.latency.dd.textContent = '—';
    connecting = true;
    cta.disabled = true;
    setCta('Conectando…');
    btnClose.disabled = false;
    vadR.disabled = pttR.disabled = textOnlyCkb.disabled = true;
    engine.disabled=wake.disabled=true;
    try {
      if(selectedConversation==='local'){
        if(!ctx.localBridge||!ctx.api)throw new Error('La voz local requiere la app de escritorio con una cuenta.');
        currentMode=readMode();localTranscriptId=0;
        localCoordinator=new LocalConversationCoordinator(ctx.localBridge,ctx.api,{
          state:state=>{if(attempt!==startAttempt)return;setStatus(state==='waiting'?'paused':state as SessionState);if(state==='waiting')statusText.textContent='Esperando Oye ASIS';},
          transcript:(role,text)=>{if(attempt===startAttempt)logTranscription(role,text,true,`local-${++localTranscriptId}`);},
          error:message=>{if(attempt===startAttempt)onError('local_voice',message);},
          sources:sources=>{if(attempt===startAttempt)transcriptBubbles.get(`assistant:local-${localTranscriptId}`)?.append(renderChatSources(sources));},
        });
        await localCoordinator.start({mode:currentMode,wakeEnabled:wake.checked,micEnabled:!textOnlyMode});
        if(attempt!==startAttempt)return;
        kvs.provider.dd.textContent='Mac + conversación remota';kvs.model.dd.textContent='Whisper · voz del sistema';kvs.rate.dd.textContent='16000 Hz · voz local';
        setControlsForStarted(true);return;
      }
      kvs.provider.dd.textContent=ctx.voiceConfig ? 'Voz en directo' : '—';kvs.model.dd.textContent=ctx.voiceConfig ? 'Configurado en tu cuenta' : '—';kvs.rate.dd.textContent=`${SAMPLE_RATE} Hz · ${MIC_SAMPLES} muestras/frame`;
      audioCtx = new AudioContext({ sampleRate: 48000 });
      const mode = readMode();
      currentMode = mode;
      pttCard.hidden = mode !== 'ptt';
      client = new AsisClient({
        url: wsUrl,
        token: ctx.getToken() ?? '',
        mode,
        audioContext: audioCtx,
        captureWorkletUrl: captureUrls.capture,
        playbackWorkletUrl: captureUrls.playback,
        micEnabled: !textOnlyMode,
        hooks: {
          onState: (state) => { if (attempt === startAttempt) setStatus(state); },
          onTranscript: (...args) => { if (attempt === startAttempt) logTranscription(...args); },
          onUsage: (...args) => { if (attempt === startAttempt) setUsage(...args); },
          onMetrics: (...args) => { if (attempt === startAttempt) setMetrics(...args); },
          onError: (code, message) => { if (attempt === startAttempt) onError(code, message); },
          onSpeakerFrame: () => undefined,
          onPlayedSamples: () => undefined,
        },
        log: (m, d) => ctx.log(m, d),
      });
      await client.start();
      if (attempt !== startAttempt) return;
      setControlsForStarted(true);
    } catch (err) {
      if (attempt !== startAttempt) return;
      audioCtx = null;
      client = null;
      const failed=localCoordinator;localCoordinator=null;void failed?.stop();
      setControlsForStarted(false);
      const failure = err as Error;
      onError('start_failed', /permission|notallowed|denied/i.test(`${failure.name} ${failure.message}`)
        ? 'No tengo permiso para usar el micrófono. Actívalo en los ajustes y vuelve a pulsar Hablar.'
        : `No he podido empezar a escucharte: ${failure.message}`);
      paintState('disconnected');
    }
  }

  async function stop(): Promise<void> {
    const attempt = ++startAttempt;
    const stopping = client;
    const stoppingLocal=localCoordinator;localCoordinator=null;camera.stop();cameraToggle.textContent='Activar cámara';cameraToggle.setAttribute('aria-pressed','false');
    client = null;
    audioCtx = null;
    setControlsForStarted(false);
    if (stopping) {
      try { await stopping.stop(); } catch (err) {
        if (attempt === startAttempt) onError('stop_failed', (err as Error).message);
      }
    }
    if(stoppingLocal){try{await stoppingLocal.stop();}catch(err){if(attempt===startAttempt)onError('stop_failed',(err as Error).message);}}
    if (attempt !== startAttempt) return;
    setStatus('disconnected');
    btnPtt.classList.remove('active');
  }

  function setupMeter(): void {
    if (meterTimer != null) return;
    meterTimer = window.setInterval(() => {
      const rms = Number(getComputedStyle(document.documentElement).getPropertyValue('--rms'));
      if (Number.isFinite(rms)) {
        meterFill.style.transform = `scaleX(${Math.max(0, Math.min(1, rms))})`;
        meterLabel.textContent = rms > 0.05 ? `Entrada activa · ${(rms * 100).toFixed(0)}%` : 'Sin entrada de audio';
      }
    }, 100);
  }

  function teardownMeter(): void {
    if (meterTimer != null) { window.clearInterval(meterTimer); meterTimer = null; }
  }

  // wire UI
  cta.addEventListener('click', () => { if (started) void stop(); else void start(); });
  btnMute.addEventListener('click', () => {
    if (!client&&!localCoordinator) return;
    const next = btnMute.getAttribute('aria-pressed') !== 'true';
    btnMute.setAttribute('aria-pressed', String(next));
    btnMute.textContent = next ? 'Reanudar micro' : 'Silenciar';
    client?.setMute(next);localCoordinator?.setMuted(next);
  });
  btnInterrupt.addEventListener('click', () => {
    void (localCoordinator?.interrupt()??client?.interrupt())?.catch((err) => onError('interrupt_failed', (err as Error).message));
  });
  btnClose.addEventListener('click', () => { void stop(); });
  btnSend.addEventListener('click', () => {
    if (cards) { void cards.submit(); return; }
    if (!client&&!localCoordinator) return;
    const t = composerInput.value;
    if (t.trim().length === 0) return;
    if(localCoordinator)void localCoordinator.sendText(t).catch(err=>onError('local_text',(err as Error).message));else client?.sendText(t);
    composerInput.value = '';
  });
  composerInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (cards||client||localCoordinator)) { e.preventDefault(); btnSend.click(); }
  });
  vadR.addEventListener('change', () => { if (!started && !connecting) { currentMode = 'vad'; pttCard.hidden = true; } });
  pttR.addEventListener('change', () => { if (!started && !connecting) { currentMode = 'ptt'; pttCard.hidden = false; } });
  textOnlyCkb.addEventListener('change', () => {
    if (started || connecting) { textOnlyCkb.checked = textOnlyMode; return; }
    textOnlyMode = textOnlyCkb.checked;
    wake.disabled=textOnlyMode;if(textOnlyMode)wake.checked=false;
    pttCard.hidden = currentMode !== 'ptt' || textOnlyMode;
  });

  function chooseText(): void {
    if (started || connecting) return;
    textOnlyCkb.checked = true;
    textOnlyCkb.dispatchEvent(new Event('change'));
    setControlsForStarted(false);
    transcriptHint.textContent = 'Pulsa Empezar a escribir para abrir el chat. El micrófono permanece apagado.';
    cta.focus();
  }
  writeButton.addEventListener('click', chooseText);
  textOnlyCkb.addEventListener('change', () => { if (!started && !connecting) setControlsForStarted(false); });

  // PTT pointer
  const down=()=>{if(localCoordinator)void localCoordinator.pttDown().catch(e=>onError('local_capture',(e as Error).message));else client?.pttDown();};
  const up=()=>{if(localCoordinator)void localCoordinator.pttUp().catch(e=>onError('local_capture',(e as Error).message));else client?.pttUp();};
  const press = (ev: Event) => { ev.preventDefault(); if ((!client&&!localCoordinator) || btnPtt.disabled) return; down(); btnPtt.classList.add('active'); };
  const release = (ev: Event) => { ev.preventDefault(); if (!client&&!localCoordinator) return; up(); btnPtt.classList.remove('active'); };
  const cancel = () => { client?.pttCancel();localCoordinator?.pttCancel();btnPtt.classList.remove('active'); };
  btnPtt.addEventListener('pointerdown', press);
  btnPtt.addEventListener('pointerup', release);
  btnPtt.addEventListener('pointerleave', (e) => { if ((e.buttons & 1) !== 0) cancel(); });
  btnPtt.addEventListener('pointercancel', cancel);
  btnPtt.addEventListener('lostpointercapture', cancel);
  btnPtt.addEventListener('contextmenu', (e) => e.preventDefault());
  const onKeyDown = (e: KeyboardEvent) => {
    if ((!client&&!localCoordinator) || currentMode !== 'ptt') return;
    if (e.code !== 'Space') return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    e.preventDefault();
    if (!e.repeat) { if (btnPtt.disabled) return; keyboardPtt = true; down(); btnPtt.classList.add('active'); }
  };
  const onKeyUp = (e: KeyboardEvent) => {
    if (e.code !== 'Space' || !keyboardPtt) return;
    keyboardPtt = false;
    e.preventDefault();
    if (!client&&!localCoordinator) return;
    up();
    btnPtt.classList.remove('active');
  };
  const onBlur = () => { keyboardPtt = false;cancel(); };
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);
  engine.addEventListener('change',()=>{
    if(started||connecting)return;
    selectedConversation=engine.value==='local'?'local':'gemini';localHelp.hidden=selectedConversation!=='local';wakeLabel.hidden=selectedConversation!=='local';wake.checked=false;
    currentMode=selectedConversation==='local'?'ptt':'vad';pttR.checked=currentMode==='ptt';vadR.checked=currentMode==='vad';pttCard.hidden=currentMode!=='ptt';
    if(selectedConversation==='local')localHelp.textContent='Voz y reconocimiento en el Mac; solo el texto viaja al servidor. Pulsa para hablar recomendado. Voz continua experimental: la voz del sistema puede provocar eco.';
  });
  cameraToggle.addEventListener('click',async()=>{
    if(cameraToggle.disabled)return;
    if(camera.active){camera.stop();cameraToggle.textContent='Activar cámara';cameraToggle.setAttribute('aria-pressed','false');return;}
    const attempt=startAttempt;cameraToggle.disabled=true;
    try{await camera.start();if(attempt!==startAttempt||!started){camera.stop();return;}if(!camera.active)return;cameraToggle.textContent='Apagar cámara';cameraToggle.setAttribute('aria-pressed','true');cameraHelp.textContent='Cámara compartida en esta conversación. La cámara del Mac no ofrece visión 360°.';}
    catch(err){if(attempt===startAttempt)cameraHelp.textContent=(err as Error).message;}
    finally{if(attempt===startAttempt)cameraToggle.disabled=!started||selectedConversation!=='gemini'||ctx.voiceConfig?.provider!=='gemini-live';}
  });

  // resolve worklet URLs + WS URL via Vite (import.meta.url) — never reach
  // for process.env or globals
  captureUrls = { capture: captureWorkletUrl, playback: playbackWorkletUrl };
  try {
    const u = new URL(ctx.serverBase);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    u.pathname = '/ws';
    wsUrl = u.toString();
  } catch { wsUrl = null; }

  setupMeter();
  setStatus('disconnected');
  // «Hablar» on Hoy is the same explicit click as here: it opens the microphone. «Escribir» only moves the cursor.
  const conversationStart = cards ? takeConversationStart() : null;
  if (conversationStart) void cards!.submit(conversationStart);
  const intent = takeConversationIntent();
  if (intent === 'text') { if (cards) setTimeout(() => composerInput.focus(), 0); else chooseText(); }
  if (intent === 'voice' && ctx.voiceConfig) void start();

  return {
    el: root,
    destroy() {
      destroyed=true;cards?.destroy();
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      void stop();
      teardownMeter();
    },
  };
}

/** Render only provider/tool-supplied references, never model-generated markup. */
export function renderChatSources(sources: ChatSource[]): HTMLElement {
  const list = h('ul', { class: 'chat-sources', 'aria-label': 'Fuentes y consultas' });
  for (const source of sources.slice(0, 20)) {
    if (!source || typeof source.title !== 'string') continue;
    const row = h('li');
    let safeUrl: string | null = null;
    try { const url = new URL(source.url ?? ''); if (['http:', 'https:'].includes(url.protocol) && !url.username && !url.password) safeUrl = url.href; } catch { /* plain reference */ }
    row.append(safeUrl ? h('a', { href: safeUrl, target: '_blank', rel: 'noopener noreferrer' }, [source.title]) : h('span', {}, [source.title]));
    const metadata = [source.connectorId, source.operationId, source.auditId].filter(value => typeof value === 'string' && value.length > 0);
    if (metadata.length) row.append(h('small', { class: 'card-sub' }, [` · ${metadata.join(' · ')}`]));
    list.append(row);
  }
  return list;
}
